/**
 * Pure, parameterized virtual-joystick model.
 *
 * MEASUREMENT-RIG PHASE 1 (see the joystick-lab report for the actual
 * measurements). This file is the single source of truth for the
 * input -> direction pipeline: origin-tracking (leash/fixed/hybrid),
 * deadzone + dominant-axis + axis-bias resolution, and hold-repeat
 * scheduling. `Joystick.tsx` consumes it directly (no parallel copy of the
 * math) so the thing the lab measures IS the thing that ships -- there is
 * no drift between "what we tuned" and "what plays".
 *
 * Everything in this file is pure: no DOM, no timers, no `Date.now()`. The
 * component owns turning real pointer events / a real setInterval into
 * calls into this module; this module owns the geometry + policy.
 */

/** The 4 real directions a joystick read can resolve to -- a neutral read
 * (inside the deadzone, or no committed axis yet) is `null`, never a 5th
 * "none" member, so callers can't accidentally treat "no direction" as a
 * direction. */
export type JoyDir = 'up' | 'down' | 'left' | 'right';

/** How the dynamic centre ("origin") the offset is measured from behaves
 * during a drag:
 * - `fixed`  -- origin is the press point for the whole gesture. Every read
 *   is an offset from where the thumb first landed.
 * - `leash`  -- origin trails the pointer, kept within `leashPx` of it (this
 *   is what ships today). Lets a small flick change direction without
 *   dragging back through centre, at the cost of the offset (and thus the
 *   distance to a direction-change) being "smeared" by wherever the leash
 *   happens to be sitting.
 * - `hybrid` -- fixed for the FIRST commit (kills the reversal tax and the
 *   thumb-arc lock-in a fresh-off-deadzone read is exposed to), then
 *   leash-like re-centering kicks in for the rest of the hold (keeps the
 *   "flick anywhere" convenience once a direction is already live).
 */
export type OriginMode = 'fixed' | 'leash' | 'hybrid';

/** Shape of the deadzone/gate test. `square` (today's shipped shape) tests
 * the Chebyshev (max-axis) distance, which is what lets a dominant-axis
 * resolver map 1:1 onto Pac's 4-way grid with no diagonal ambiguity and
 * lets the knob visually reach the corners of a square base. `circle`
 * tests the Euclidean distance instead (a round gate/deadzone). */
export type Gate = 'square' | 'circle';

/** Where the KNOB's RENDERED position is measured from -- deliberately a
 * SEPARATE concern from `originMode` above (which governs what the
 * RESOLVER sees for direction purposes; the leash origin-tracking stays
 * exactly as measured/validated regardless of this setting):
 *  - `leash` -- knob offset = the same leash-relative `{dx,dy}` the
 *    resolver reads, auto-amplified by `radius/leashPx` so it visually
 *    swings out to fill the gate (today's shipped behaviour). `knobGain`
 *    below is IGNORED in this mode -- the amplification is a derived
 *    quantity (must always exactly fill the radius regardless of what
 *    `leashPx` is), not a free parameter. Diagnosed (joystick-lab phase 3)
 *    to detach the knob from the thumb: the knob reaches this clamp ~0.7px
 *    past the deadzone exit and then sits pinned at the rim for the rest of
 *    a real (22-45px) push, while the leash offset itself saturates at
 *    `leashPx`.
 *  - `press` -- knob offset = the raw thumb offset from the ORIGINAL PRESS
 *    POINT (an anchor that never moves for the whole gesture, unlike the
 *    leash-relative origin) times `knobGain`. `knobGain: 1` puts the knob
 *    directly under the thumb's actual contact point (clamped to the gate,
 *    same as always) -- an honest render, decoupled from the (separately
 *    tuned, separately validated) resolver's leash. */
export type VisualOrigin = 'leash' | 'press';

export interface JoystickParams {
  /** Knob must move at least this far from the origin (px, shape per
   * `gate`) before ANY direction is considered "pressed". */
  deadzonePx: number;
  /** Origin-trail radius (px) used when `originMode` is leashing. */
  leashPx: number;
  /** Axis-switch bias: the off-axis displacement must exceed the committed
   * axis's by this FRACTION before the resolved direction switches axes.
   * A sign flip along the SAME axis is always immediate, never gated. */
  axisBias: number;
  /** While a direction is held, re-assert it on this interval (ms). */
  repeatMs: number;
  originMode: OriginMode;
  gate: Gate;
  /** See `VisualOrigin` above. Purely a rendering concern -- never affects
   * `resolveDir`/direction resolution, only the `knob` field returned by
   * `updateJoystick`/`runGestureDetailed`. */
  visualOrigin: VisualOrigin;
  /** Multiplier applied to the raw press-relative thumb offset when
   * `visualOrigin === 'press'`. Ignored when `visualOrigin === 'leash'`
   * (see `VisualOrigin` above -- that mode derives its own amplification).
   * `1` = knob rendered directly under the thumb's contact point. */
  knobGain: number;
  /** Knob diameter as a FRACTION of the gate's full side/diameter -- MUST
   * match `.joystick-knob`'s CSS width/height (index.css). Used to clamp
   * the knob's rendered centre so it never renders outside the gate (a
   * round radial clamp would forbid the square gate's corners, so this is
   * a per-axis clamp, same as the deadzone/gate shape). Single source of
   * truth for this value -- was previously duplicated as a local
   * `KNOB_RATIO` constant in Joystick.tsx. */
  knobRatio: number;
  /** SETTLE WINDOW (real-trace re-tune, see DEFAULT_PARAMS doc comment
   * below): when the resolver would commit a NEW direction that differs
   * from the CURRENTLY COMMITTED one (a "turn"), the commit is not applied
   * on the first sample that crosses the axis-bias threshold -- instead a
   * short window opens and the MOST DECISIVE sample observed within it
   * (see `earlyExitMargin`/the `margin` computation in
   * `stepDirectionWithSettle`) is the one that actually commits. HOLDS
   * (samples that keep resolving to the already-committed direction) and
   * the very FIRST commit of a fresh gesture (no direction committed yet)
   * are never delayed by this -- only genuine direction CHANGES are. `0`
   * disables the window entirely (every change commits on its first
   * qualifying sample, i.e. the pre-this-task behavior). */
  settleMs: number;
  /** A candidate change whose `margin` (see `stepDirectionWithSettle`,
   * `(major-minor)/(major+minor)` of the axis-dominant sample, 0 = dead
   * 45deg tie, 1 = pure cardinal) is already at or above this threshold
   * commits IMMEDIATELY, skipping the settle window -- a clean cardinal
   * flick must stay instant, not eat `settleMs` of latency it doesn't need.
   * `1` (or anything > the max possible margin) effectively disables the
   * early exit and every change waits the full window. */
  earlyExitMargin: number;
}

/**
 * PHASE 3 winner (measured, not assumed -- see tests/joystick-lab/lab.test.ts's
 * "PHASE 3 STEP 1/2" describe blocks for the actual numbers this rationale
 * is built on). This is the joystick-lab's config "D": knob-under-thumb
 * rendering + a gate sized to a real thumb envelope + the resolver optimum
 * the grid search already found -- previously separate, individually
 * validated wins, now shipped together because measuring them together
 * showed neither compromises the other:
 *
 *  - `deadzonePx: 10, leashPx: 20, axisBias: 0.15` -- the resolver optimum
 *    the "Best-config search" grid search below already found (-17.26%
 *    composite score vs the old shipped values, on the mixed
 *    reversal+turn script, both hands, 20 seeds). `originMode: 'leash'` and
 *    `gate: 'square'` are UNCHANGED -- the rig proved the leash isn't the
 *    problem (H1 REFUTED) and a square gate remains right for a 4-way grid.
 *  - `visualOrigin: 'press', knobGain: 1` -- the KNOB renders directly
 *    under the thumb's actual contact point (offset from the fixed press
 *    point, gain 1:1), not an 8x-amplified projection of the leash's tiny
 *    trailing offset. This is a purely VISUAL fix, deliberately decoupled
 *    from the resolver above (which still reads the leash-relative offset
 *    for direction purposes) -- PHASE 3 STEP 1 measured the OLD
 *    `visualOrigin: 'leash'` rendering diverging from the thumb by a
 *    P50 of ~52px (worse than the ~25px predicted) and sitting AT ITS
 *    CLAMP for ~96% of every active (post-deadzone) sample -- the knob was
 *    never under the thumb, it was flung to the rim ~0.7px after the
 *    deadzone and pinned there.
 *
 * GATE-SIZE RE-TUNE (real-user feedback, see tests/joystick-lab/gateLab.test.ts):
 * PHASE 3 shrank the gate to a ~57px radius (`.joystick-base` CSS multiplier
 * `1.5`), sized to the joystick-lab's MODELED thumb envelope (22-45px push,
 * 50px hard cap) -- a real operator playing on a real phone reported the
 * box was too small ("needs to be bigger", "still miss turns sometimes"),
 * and testing the ORIGINAL model's own doc comment ("plausible, not
 * measured") against the actual shipped DOM contract exposed why: none of
 * the direction-resolution metrics above (`resolveDir`/`stepOrigin`) ever
 * read `radius` for anything but an early `radius <= 0` bail-out, so the
 * PHASE 3 grid search was structurally blind to gate size -- it could not
 * have caught this. The real mechanism is in `Joystick.tsx`: `.joystick-base`
 * is a FIXED-position DOM rect, and `onPointerLeave` -> `handlePointerEnd()`
 * fires from the pointer's REAL screen position (pointer capture does not
 * redirect boundary events, per the Pointer Events spec) the instant a
 * thumb strays outside that rect -- and PERMANENTLY freezes the gesture
 * (`pointerIdRef.current = null` makes `handlePointerMove` drop every
 * further move, even ones that re-enter the box, until a fresh lift +
 * re-press). `gateLab.test.ts` reproduces exactly that freeze and measures
 * it: at the OLD `1.5` multiplier, on the SMALLEST real breakpoint
 * (`--dpad-btn` clamped to its 44px touch-target floor, radius=33px), 85%
 * of simulated pushes exited the box at least once and `missedIntentRate`
 * jumped from 0.0000 to 0.6800 (composite score 6.20 -> 30.68) -- a
 * catastrophic, gate-size-caused miss rate the PHASE 3 metrics could never
 * see. A grid search over gate multiplier (1.5/1.8/2.0/2.2/2.5) x
 * deadzonePx/leashPx/axisBias found multiplier `2.5` is the smallest tested
 * size that reaches 0% boundary-exit at that SAME worst-case 44px
 * breakpoint (radius=55px, just past the modeled worst-case reach of
 * ~54px = 50px envelope + 4px press scatter) -- and the resolver optimum
 * above (10/20/0.15) is RE-CONFIRMED best at this new gate size too (a
 * finer axisBias sweep found a ~1% score improvement at 0.22, judged noise-
 * floor-adjacent and not worth the change). An explicit sensitivity check
 * against a deliberately BIGGER modeled thumb reach (30-65px push, 75px
 * envelope, vs. the default 22-45px/50px -- since the default is
 * documented as "plausible, not measured", not ground truth) confirms the
 * bigger gate is a real, assumption-independent improvement under EITHER
 * reach model (worst-case score 30.68 -> 6.20 under the default reach,
 * 38.20 -> 22.86 under the bigger reach) -- though it does NOT fully
 * eliminate the freeze under the more pessimistic reach assumption (55%
 * boundary-exit remains); a still-bigger gate would help further but 2.5
 * is the largest multiplier this investigation tested and the layout still
 * needs verifying against it at every breakpoint (see index.css). `.joystick-base`
 * therefore moved from `calc(var(--dpad-btn, 44px) * 1.5)` to `* 2.5`
 * (index.css) -- this is a SIMULATION result, not a guarantee: the real
 * proof is the operator playing it again.
 *
 * AXIS-BIAS RE-TUNE ON REAL CAPTURED GESTURES (supersedes the
 * grid-search-on-synthetic-thumb rationale above for `axisBias`
 * specifically -- `deadzonePx: 10`/`leashPx: 20` are UNCHANGED and still
 * rest on that grid search): the operator played 9 rounds on a real phone
 * with turn-intent tracing on and tapped a "missed a turn" marker each
 * time (traces: tests/joystick-lab/traceReplay.test.ts's fixtures,
 * originally server/.data/traces/HSWAD5-*.json, 1481 real joystick
 * samples). All 26 `SUPPRESSED_AXIS_BIAS` samples in the 1.2s window
 * before the 9 markers were near-45-degree flicks where the new axis was
 * ALREADY larger than the held axis, just not by the shipped 15% margin --
 * exactly the failure mode `axisBias` gates, not the leash/deadzone/gate
 * levers the earlier synthetic investigations tuned. The synthetic
 * `humanThumb` rig had already been wrong TWICE about this control (drove
 * a gate SHRINK that made missed turns worse, and modelled a freeze bug a
 * differential e2e later disproved), so this re-tune is measured against
 * the REAL captured samples instead, via
 * `tests/joystick-lab/traceReplay.ts`'s replay harness (re-runs the exact
 * recorded `dx`/`dy` stream through the SHIPPED resolver at a candidate
 * `axisBias`; sanity-gated to reproduce the ORIGINALLY RECORDED
 * outcome+direction sequence exactly at the shipped 0.15 -- see that
 * file's "REPLAY FIDELITY SANITY GATE" test, 0 mismatches across all 1481
 * real samples). Sweeping `axisBias` over {0.15, 0.12, 0.10, 0.08, 0.06,
 * 0.05, 0.04, 0.03, 0.02, 0.0} against those real samples: recovered turns
 * (of the 26) rise from 0 (@0.15) through 8/12/15/19/21/22/23 to 25/26
 * (@0.02) with ZERO rapid A-B-A oscillations (<200ms) added over the
 * baseline at every value down to and including 0.02 -- then a real twitch
 * cost appears below it (3 oscillations @0.01, 7-8 @0.005/0.0, confirmed
 * across 150/200/300ms oscillation-window widths), while the 26th/last
 * turn only recovers at that cost. `0.02` is the knee: it recovers 25 of
 * the 26 real missed turns (96%) with no measured twitch regression, and
 * is the value shipped here -- the one turn left unrecovered at this
 * setting is a genuine, disclosed tradeoff, not oversold as "fixed". The
 * synthetic mixed-script lab (lab.test.ts) was re-run at this value and
 * did not regress (`wrongDirRate`/`score` both finite and comparable to
 * the 0.15 baseline; see traceReplay.test.ts's "CHOSEN VALUE" describe
 * block) -- the two rigs AGREE here, unlike the gate-size investigation
 * above. As with the gate re-tune, this is still an OFFLINE re-run of real
 * gestures, not new live play: the real proof is the operator playing it
 * again.
 *
 * SETTLE WINDOW (supersedes NOTHING above -- `deadzonePx`/`leashPx`/gate/
 * visual fields are all still exactly as the sections above found them;
 * this ADDS `settleMs`/`earlyExitMargin` and is the reason those two exist
 * at all): a second real-user report after the 0.15 -> 0.02 re-tune above
 * ("feels better, still miss some... it turns wrong way than intended") plus
 * a second capture session (`server/.data/traces/VC9CFJ-*.json`, 9 more
 * marker traces, copied here as `tests/fixtures/traces/VC9CFJ-*.json`,
 * captured AT the 0.02 fix) measured on the REAL commits (margin =
 * (along-cross)/(along+cross) of the axis the commit landed on, 0 = dead
 * 45deg tie, 1 = pure cardinal, at the moment of commit): lowering
 * `axisBias` had roughly DOUBLED the fraction of direction commits landing
 * on a near-coin-flip margin (<0.10): HSWAD5 (0.15) 28.8%, HSWAD5-at-0.02
 * 43.3%, VC9CFJ (already at 0.02) 45.6% -- i.e. `axisBias` alone was
 * trading "misses" for "wrong-direction coin-flips", not eliminating
 * ambiguity. But within the ~100ms AFTER a commit, decisively better
 * samples routinely existed (see `stepDirectionWithSettle`'s `margin`) --
 * the resolver was committing on the FIRST post-threshold sample while the
 * thumb was still travelling through the diagonal, not because the gesture
 * itself was ambiguous. `settleMs`/`earlyExitMargin` fix the TIMING instead
 * of re-fighting `axisBias`: a candidate direction CHANGE (never a
 * HOLD/repeat, never a fresh gesture's first commit) opens a short window
 * and commits to the MOST DECISIVE sample seen in it, not the first.
 *
 * The task brief's own hypothesis was that a settle window would let
 * `axisBias` be raised back up (since the gesture becomes decisive, genuine
 * ties could be re-rejected) -- MEASURED AGAINST THE REAL TRACES, this did
 * NOT hold: `axisBias` still gates whether a candidate change is even
 * OPENED (via `resolveDir`'s existing hysteresis, unchanged), so raising it
 * suppresses real turns' candidate-opening before the settle window ever
 * gets a chance to pick a better sample -- sweeping `(settleMs,
 * earlyExitMargin, axisBias)` on both real corpora showed raising
 * `axisBias` even to 0.05 roughly HALVES `recoveredTurns` (HSWAD5's 26
 * session-1 pre-miss targets: 24/26 at 0.02 -> 11/26 at 0.05) for only a
 * modest further coin-flip improvement -- a regression the task brief's own
 * "must not regress" gate forbids. So `axisBias: 0.02` is UNCHANGED here;
 * only the settle window is new. Sweeping `settleMs` x `earlyExitMargin` at
 * the unchanged `axisBias: 0.02` found `settleMs: 100`, `earlyExitMargin:
 * 0.3` as the knee (see traceReplay.test.ts's "SETTLE WINDOW SWEEP" for the
 * full table): vs the no-settle baseline, coin-flip rate drops HSWAD5
 * 43.3% -> 20.3%, VC9CFJ 45.6% -> 7.8%; `recoveredTurns` holds at 24/26
 * (vs 25/26 pre-settle, well within the corpus's own noise); rapid A-B-A
 * oscillations (checked at 150/200/300ms windows) never rise on either
 * corpus, and one PRE-EXISTING 300ms-window oscillation on HSWAD5 actually
 * disappears; added latency (only on genuine changes, never on holds) is
 * P50 ~17-33ms, P90/max the full `settleMs`. `disagreements` (does the
 * committed direction match the independently-recomputed most-decisive
 * sample in the actual window used) is 0/64 on both corpora -- the
 * mechanism does exactly what it claims, unlike the pre-settle-window
 * baseline's 4/67 "the flick pointed one way, the resolver committed
 * another" finding the task brief opened with. `earlyExitMargin: 0.3` (well
 * below any clean cardinal flick's margin, which sits near 1.0) keeps a
 * decisive push instant -- see `stepDirectionWithSettle`'s doc comment.
 * As with every re-tune above, this is an OFFLINE re-run of real captured
 * gestures: the real proof is the operator playing it again.
 *
 * Changing this constant changes shipped behaviour, by design.
 */
export const DEFAULT_PARAMS: JoystickParams = {
  deadzonePx: 10,
  leashPx: 20,
  axisBias: 0.02,
  repeatMs: 110,
  originMode: 'leash',
  gate: 'square',
  visualOrigin: 'press',
  knobGain: 1,
  knobRatio: 0.52,
  settleMs: 100,
  earlyExitMargin: 0.3,
};

export interface Point {
  x: number;
  y: number;
}

/** A single pointer sample. `t` is milliseconds, monotonic within one
 * gesture (does not need to start at 0). `x`/`y` are client-space
 * coordinates (screen space: +y is DOWN, matching pointer/client
 * coordinates), same convention as the DOM PointerEvent this stands in
 * for. */
export interface Sample {
  t: number;
  x: number;
  y: number;
}

/** A direction the joystick emitted (would call `onDir` with), INCLUDING
 * hold-repeat re-assertions, tagged with the time it fired at. */
export interface Emission {
  t: number;
  dir: JoyDir;
}

/**
 * The single-read resolver (was `resolveJoyDir` inline in Joystick.tsx),
 * now parameterized. `dx`/`dy` are the offset from the origin in CSS px,
 * `radius` is half the gate's side/diameter in the same units. Returns
 * `null` inside the deadzone (no direction), otherwise the dominant-axis
 * cardinal direction -- Pac is a 4-way grid, so a square gate +
 * dominant-axis resolver maps 1:1 and kills diagonal ambiguity entirely
 * (no diagonal reads are possible).
 *
 * `prevDir` is the direction currently considered active (or `null`) --
 * the axis-switch bias is measured against it: once an axis (horizontal or
 * vertical) is committed, the OTHER axis's displacement must exceed the
 * committed axis's by `params.axisBias` before the resolved direction
 * switches axes, so an offset resting near a 45-degree diagonal doesn't
 * flip-flop on every sub-pixel jitter of a real thumb. A sign flip along
 * the SAME axis (e.g. right -> left) is always immediate -- there is no
 * bias against reversing within one axis. Pass `null` for a fresh read
 * (e.g. right after a deadzone exit) -- this is exactly the read a thumb
 * arc (H2) can exploit, since a fresh read has no bias protecting it.
 */
export function resolveDir(
  dx: number,
  dy: number,
  radius: number,
  prevDir: JoyDir | null,
  params: JoystickParams,
): JoyDir | null {
  if (radius <= 0) return null;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  const inDeadzone = params.gate === 'circle' ? Math.hypot(dx, dy) < params.deadzonePx : Math.max(ax, ay) < params.deadzonePx;
  if (inDeadzone) return null;

  const horiz = (): JoyDir => (dx > 0 ? 'right' : 'left');
  const vert = (): JoyDir => (dy > 0 ? 'down' : 'up');

  if (prevDir) {
    const prevHoriz = prevDir === 'left' || prevDir === 'right';
    if (prevHoriz) {
      if (ay > ax * (1 + params.axisBias)) return vert();
      return horiz(); // stay horizontal, allow sign flip
    } else {
      if (ax > ay * (1 + params.axisBias)) return horiz();
      return vert();
    }
  }
  return ax >= ay ? horiz() : vert();
}

/**
 * "How decisive is this offset" -- `(major-minor)/(major+minor)` of the two
 * axis magnitudes, 0 = sitting exactly on a 45deg diagonal (a dead tie
 * between axes), 1 = pure cardinal (one axis is zero). Used both to decide
 * the SETTLE WINDOW's early exit and to pick the "most decisive sample"
 * within an open window. Guarded against `major+minor === 0`, though that
 * can't happen for a non-deadzone offset in the `square` gate (one axis
 * must already exceed `deadzonePx > 0`); kept defensive for `circle`, where
 * the deadzone test is Euclidean and a (0, ~deadzonePx) offset is possible
 * with `minor` near 0 but `major` still > 0, never both 0.
 */
export function axisMargin(ax: number, ay: number): number {
  const major = Math.max(ax, ay);
  const minor = Math.min(ax, ay);
  return major + minor > 0 ? (major - minor) / (major + minor) : 0;
}

export interface DirStepResult {
  dir: JoyDir | null;
  changed: boolean;
  pending: PendingSettle | null;
  /** The `axisMargin` of the sample that actually decided a commit --
   * present iff `changed && dir !== null` (a real direction commit
   * happened on this step). For an immediate commit (HOLD reverting,
   * first-ever press, or an early-exit) this is that one sample's own
   * margin; for a window resolving (early-exit on a LATER sample, or a
   * time-based deadline) this is the WINNING sample's margin, which may
   * not be the sample that opened the window. Used by the real-trace
   * sweep (`traceReplay.ts`) to report "how decisive was the commit". */
  margin?: number;
}

/**
 * The settle-window-aware direction step: wraps `resolveDir` (unchanged --
 * axis-bias hysteresis still gates WHETHER a change is even a candidate,
 * exactly as before) with the settle window that decides WHEN a qualifying
 * change actually commits. Shared by the live `updateJoystick` (via
 * `stepOrigin`'s leash-adjusted `dx`/`dy`) and the real-trace replay harness
 * (`tests/joystick-lab/traceReplay.ts`, which already has leash-adjusted
 * `dx`/`dy` recorded at capture time and so calls this directly) -- one
 * implementation of "how a direction change settles", not two that could
 * drift apart.
 *
 * Behavior:
 *  - Inside the deadzone (`resolveDir` returns `null`): any pending window
 *    is dropped (the thumb returned toward centre; whatever it was flicking
 *    toward is moot) and `dir` goes to `null`, exactly as pre-settle-window.
 *  - The raw resolved direction matches the already-committed `prevDir`
 *    (a HOLD/repeat, or a pending window's candidate reverting to the old
 *    direction): immediate, pending dropped. Holds are NEVER delayed.
 *  - `prevDir === null` (the very first commit of a fresh gesture, nothing
 *    to "change" away from yet): immediate, matching every test/behavior
 *    that predates this feature -- the settle window only ever applies to
 *    a change between two already-established directions.
 *  - Otherwise this raw direction differs from `prevDir` -- a genuine
 *    candidate change (it already passed the axis-bias hysteresis check
 *    inside `resolveDir`). If its `axisMargin` already clears
 *    `params.earlyExitMargin`, commit immediately (a clean flick pays no
 *    settle latency). Otherwise open (or extend) the window: track the
 *    best-margin candidate seen so far (which may end up being a LATER,
 *    even MORE decisive sample, and may even point a different candidate
 *    direction than the one that opened the window -- see the module doc
 *    comment / task brief), and commit once either the running best clears
 *    `earlyExitMargin` or `t` reaches `startT + params.settleMs`.
 */
export function stepDirectionWithSettle(
  dx: number,
  dy: number,
  radius: number,
  t: number,
  prevDir: JoyDir | null,
  pending: PendingSettle | null,
  params: JoystickParams,
): DirStepResult {
  const rawDir = resolveDir(dx, dy, radius, prevDir, params);

  if (rawDir === null) {
    return { dir: null, changed: prevDir !== null, pending: null };
  }
  if (rawDir === prevDir) {
    return { dir: rawDir, changed: false, pending: null };
  }
  if (prevDir === null) {
    return { dir: rawDir, changed: true, pending: null, margin: axisMargin(Math.abs(dx), Math.abs(dy)) };
  }

  // A genuine candidate change (differs from the committed `prevDir`).
  // Fold this sample into whatever window is already open (or start a
  // fresh one anchored at THIS sample, `startT: t`, if none was) and THEN
  // evaluate both exit conditions uniformly -- critically, the deadline
  // check (`t - startT >= settleMs`) runs on the OPENING sample too, not
  // only on later ones, so `settleMs: 0` degenerates to "every qualifying
  // change commits on its first sample" (the pre-this-task behavior)
  // instead of silently opening a window that can only ever be resolved by
  // a LATER sample -- there might not be one.
  const sampleMargin = axisMargin(Math.abs(dx), Math.abs(dy));
  const startT = pending?.startT ?? t;
  const bestMargin = pending !== null && pending.bestMargin > sampleMargin ? pending.bestMargin : sampleMargin;
  const bestDir = pending !== null && pending.bestMargin > sampleMargin ? pending.bestDir : rawDir;

  if (bestMargin >= params.earlyExitMargin || t - startT >= params.settleMs) {
    const changed = bestDir !== prevDir;
    return { dir: bestDir, changed, pending: null, margin: changed ? bestMargin : undefined };
  }
  return { dir: prevDir, changed: false, pending: { startT, bestMargin, bestDir } };
}

/**
 * Forces a still-open settle window to commit its best-so-far candidate,
 * for when `params.settleMs` elapses with NO further pointer sample to
 * naturally re-evaluate it (a real thumb can flick decisively then hold
 * still) -- the live component drives this from a `setTimeout` armed for
 * `pending.startT + params.settleMs` (see Joystick.tsx), and the offline
 * batch replay (`runGestureDetailed` / `traceReplay.ts`) drives it from the
 * same deadline computed against the next sample's timestamp (or the end of
 * the stream), so both paths resolve an unresolved window identically.
 */
export function flushPendingSettle(prevDir: JoyDir | null, pending: PendingSettle | null): DirStepResult {
  if (pending === null) return { dir: prevDir, changed: false, pending: null };
  const changed = pending.bestDir !== prevDir;
  return { dir: pending.bestDir, changed, pending: null, margin: changed ? pending.bestMargin : undefined };
}

/** TURN-INTENT TRACE diagnostic classifier (additive -- does not affect
 * `resolveDir`/`updateJoystick`/`runGesture` in any way, and is only ever
 * called from Joystick.tsx when tracing is enabled, see trace.ts). Explains
 * WHY a single pointer sample did or didn't result in a direction change,
 * as one of the client reason codes trace.ts's `ClientSampleOutcome`
 * mirrors:
 *  - `'EMITTED'` -- the resolved direction differs from `prevDir` (this is
 *    exactly `UpdateResult.changed && dir !== null` -- the `onDir` firing
 *    condition in Joystick.tsx).
 *  - `'SUPPRESSED_DEADZONE'` -- `resolveDir` returned `null` (inside the
 *    gate).
 *  - `'SUPPRESSED_AXIS_BIAS'` -- the resolved direction is UNCHANGED from
 *    `prevDir`, but a bias-FREE dominant-axis read (`ax >= ay ? horiz :
 *    vert`, i.e. `resolveDir`'s own no-`prevDir` branch) would have picked
 *    the OTHER axis -- the axis-switch hysteresis is what's actively
 *    holding the old direction here, not "the thumb simply didn't move
 *    enough to suggest a change".
 *  - `'SUPPRESSED_SAME_DIR'` -- resolved direction unchanged AND the
 *    bias-free read agrees -- genuinely no ambiguity, the thumb is just
 *    still pushing the same way.
 * Deliberately duplicates a few lines of `resolveDir`'s own arithmetic
 * (the bias-free axis check) rather than reaching into its private
 * decision -- this function is diagnostics-only, never consulted by
 * `resolveDir` itself, so there is no risk of the real resolver's behavior
 * drifting to match a debug classifier; see trace.ts's `SUPPRESSED_REPEAT_
 * THROTTLE` doc comment for the one reason code in the task's taxonomy this
 * shipped resolver has no code path for at all (not modeled here, either).
 */
export function classifySampleOutcome(
  dx: number,
  dy: number,
  radius: number,
  prevDir: JoyDir | null,
  resolvedDir: JoyDir | null,
): 'EMITTED' | 'SUPPRESSED_DEADZONE' | 'SUPPRESSED_AXIS_BIAS' | 'SUPPRESSED_SAME_DIR' {
  if (resolvedDir === null) return 'SUPPRESSED_DEADZONE';
  if (resolvedDir !== prevDir) return 'EMITTED';
  if (radius <= 0) return 'SUPPRESSED_SAME_DIR';
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  const naiveDir: JoyDir = ax >= ay ? (dx > 0 ? 'right' : 'left') : dy > 0 ? 'down' : 'up';
  return naiveDir !== resolvedDir ? 'SUPPRESSED_AXIS_BIAS' : 'SUPPRESSED_SAME_DIR';
}

/** Origin-tracking state carried between reads of one continuous gesture
 * (press -> move* -> release). `committedOnce` is only meaningful for
 * `originMode: 'hybrid'` -- it flips true the first time a direction
 * commits, at which point hybrid switches from fixed to leash-like
 * trailing for the remainder of the hold. */
/** A settle window in progress -- a candidate direction change has been
 * seen (differs from the currently committed `dir`) but not yet committed;
 * `bestMargin`/`bestDir` track the MOST DECISIVE candidate sample observed
 * so far in the window (see `stepDirectionWithSettle`), not the first one.
 * `startT` is the time the window opened (the first candidate sample),
 * used both to pick the settle deadline (`startT + params.settleMs`) and to
 * report "added latency" (commit time minus `startT`). */
export interface PendingSettle {
  startT: number;
  bestMargin: number;
  bestDir: JoyDir;
}

export interface JoystickState {
  origin: Point | null;
  dir: JoyDir | null;
  committedOnce: boolean;
  /** The press point (first point of the gesture) -- unlike `origin`,
   * NEVER moves for the rest of the gesture even when `originMode` leashes.
   * This is what `visualOrigin: 'press'` measures the knob from, and what
   * the joystick-lab's `knobThumbDivergencePx` metric measures the thumb's
   * actual contact point from -- a real thumb's physical base doesn't
   * relocate mid-push, so this is the honest "where did the thumb actually
   * go" reference. */
  anchor: Point | null;
  /** SETTLE WINDOW state -- `null` when no direction-change candidate is
   * currently being evaluated (the common case: holds/repeats never open
   * one). See `PendingSettle`/`stepDirectionWithSettle`. */
  pending: PendingSettle | null;
}

export function initJoystickState(): JoystickState {
  return { origin: null, dir: null, committedOnce: false, anchor: null, pending: null };
}

/** Applies the origin-tracking rule for one step and returns the (possibly
 * moved) origin plus the resulting offset. Shared by `updateJoystick` (the
 * live, one-sample-at-a-time entry point Joystick.tsx drives) and
 * `runGesture` (the batch replay entry point the lab drives) so there is
 * exactly one implementation of "how the origin moves". */
function stepOrigin(
  origin: Point,
  point: Point,
  params: JoystickParams,
  committedOnce: boolean,
): { origin: Point; dx: number; dy: number } {
  let dx = point.x - origin.x;
  let dy = point.y - origin.y;
  let dist = Math.hypot(dx, dy);
  const leashing = params.originMode === 'leash' || (params.originMode === 'hybrid' && committedOnce);
  if (leashing && dist > params.leashPx) {
    const k = (dist - params.leashPx) / dist;
    origin = { x: origin.x + dx * k, y: origin.y + dy * k };
    dx = point.x - origin.x;
    dy = point.y - origin.y;
  }
  return { origin, dx, dy };
}

/** Clamps a single axis offset to the knob's per-axis square gate limit
 * (see `knobRatio`'s doc comment for why per-axis, not a round radial
 * clamp). Shared by the live and batch-replay entry points so there is
 * exactly one implementation of "how the knob clamps". */
function clampAxis(v: number, maxOffset: number): number {
  return Math.max(-maxOffset, Math.min(maxOffset, v));
}

/**
 * Computes the knob's rendered offset (clamped to the gate) per
 * `params.visualOrigin` -- see that type's doc comment for the two modes.
 * `dx`/`dy` are the leash-relative offset (what the resolver sees, same as
 * `resolveDir`'s inputs); `thumb` is the raw, unclamped offset from the
 * fixed press-point anchor. Pure geometry, no state.
 */
function computeKnob(dx: number, dy: number, thumb: Point, radius: number, params: JoystickParams): Point {
  let kx: number;
  let ky: number;
  if (params.visualOrigin === 'press') {
    kx = thumb.x * params.knobGain;
    ky = thumb.y * params.knobGain;
  } else {
    const kscale = params.leashPx > 0 ? radius / params.leashPx : 1;
    kx = dx * kscale;
    ky = dy * kscale;
  }
  const maxOffset = radius * (1 - params.knobRatio);
  return { x: clampAxis(kx, maxOffset), y: clampAxis(ky, maxOffset) };
}

export interface UpdateResult {
  state: JoystickState;
  dx: number;
  dy: number;
  dir: JoyDir | null;
  /** True iff `dir` differs from the state's PREVIOUS `dir` -- this is the
   * "fire onDir" signal (a same-direction read never re-fires; that's what
   * the hold-repeat timer is for). */
  changed: boolean;
  /** The knob's rendered offset (clamped to the gate), per
   * `params.visualOrigin` -- see `computeKnob`. */
  knob: Point;
  /** The raw, UNCLAMPED thumb offset from the fixed press-point anchor --
   * "where the thumb actually is", independent of any leash/clamp/gain.
   * Used by the joystick-lab's visual-fidelity metrics (and by `knob`
   * itself when `visualOrigin === 'press'`). */
  thumb: Point;
}

/**
 * Live, single-sample entry point: given the current gesture state and one
 * new pointer position, returns the next state plus the resolved
 * direction. On the FIRST call of a gesture (state.origin === null), the
 * origin is initialized to the press point (so the first read is always
 * dx=dy=0, i.e. inside the deadzone) -- callers don't need to special-case
 * pointerdown. `t` is a monotonic clock reading (ms, e.g. `performance.now()`
 * live, or a `Sample.t` in replay) -- needed by the SETTLE WINDOW
 * (`stepDirectionWithSettle`) to know how long a pending direction-change
 * candidate has been open; harmless for direction resolution otherwise.
 */
export function updateJoystick(state: JoystickState, point: Point, t: number, radius: number, params: JoystickParams): UpdateResult {
  const origin = state.origin ?? point;
  const anchor = state.anchor ?? point;
  const { origin: newOrigin, dx, dy } = stepOrigin(origin, point, params, state.committedOnce);
  const thumb: Point = { x: point.x - anchor.x, y: point.y - anchor.y };
  const step = stepDirectionWithSettle(dx, dy, radius, t, state.dir, state.pending, params);
  const committedOnce = state.committedOnce || step.dir !== null;
  const knob = computeKnob(dx, dy, thumb, radius, params);
  return { state: { origin: newOrigin, dir: step.dir, committedOnce, anchor, pending: step.pending }, dx, dy, dir: step.dir, changed: step.changed, knob, thumb };
}

/**
 * Replays a pointer gesture through the model. Pure: no DOM, no timers, no
 * clock -- `samples[0]` stands in for pointerdown, later samples for
 * pointermove, and the LAST sample's timestamp stands in for the moment of
 * release (repeats are scheduled only strictly BEFORE it; there is no
 * explicit "pointerup" sample type because release itself never emits a
 * direction, see Joystick.tsx's `recenter()`).
 *
 * Between two consecutive samples, real time still passes -- if a
 * direction is held long enough, the component's hold-repeat interval
 * fires one or more times with no new pointermove at all. This function
 * reproduces that by scheduling repeat emissions at
 * `commitTime + n*repeatMs` and flushing every scheduled repeat whose time
 * falls strictly before the NEXT sample's timestamp, exactly mirroring
 * `setInterval(() => onDir(dir), repeatMs)` being started on commit and
 * `clearInterval`'d on the next direction change (see `stopRepeat()` in
 * Joystick.tsx).
 *
 * Returns every direction the component would have emitted (onDir calls),
 * in chronological order, including repeats.
 *
 * Thin wrapper over `runGestureDetailed` (kept as its own named export,
 * unchanged signature, for existing callers/tests) -- the detailed variant
 * does the exact same emission-scheduling walk and additionally records
 * per-sample knob/thumb geometry for the joystick-lab's visual-fidelity
 * metrics, so there is exactly one implementation of "how a gesture
 * replays", not two that could drift apart.
 */
export function runGesture(samples: Sample[], params: JoystickParams, radius: number): Emission[] {
  return runGestureDetailed(samples, params, radius).emissions;
}

/** One replayed pointer sample's resolved geometry -- everything
 * `runGestureDetailed` records per INPUT sample (not per emission; hold-
 * repeats re-assert an already-resolved direction with no new geometry, so
 * they are not given their own detail record). */
export interface DetailedSample {
  t: number;
  /** Resolved direction at this sample (post deadzone/dominant-axis/axis-
   * bias), `null` if inside the deadzone / no axis committed yet. */
  dir: JoyDir | null;
  /** The knob's rendered offset (clamped to the gate) at this sample. */
  knob: Point;
  /** The raw, unclamped thumb offset from the press-point anchor at this
   * sample. */
  thumb: Point;
}

/**
 * Batch-replay entry point with full per-sample knob/thumb geometry, used
 * by the joystick-lab's visual-fidelity metrics (`knobThumbDivergencePx`,
 * `knobSaturationFraction`, `knobGainLinearity` in metrics.ts). Identical
 * emission-scheduling behaviour to `runGesture` (see its doc comment for
 * the repeat-scheduling contract) -- this is the one real implementation,
 * `runGesture` is a thin projection of its `emissions` field.
 */
export function runGestureDetailed(samples: Sample[], params: JoystickParams, radius: number): { emissions: Emission[]; details: DetailedSample[] } {
  const emissions: Emission[] = [];
  const details: DetailedSample[] = [];
  if (samples.length === 0) return { emissions, details };

  let state = initJoystickState();
  let repeatNextAt: number | null = null;

  /** Flushes every scheduled "no new pointer sample needed" event (hold-
   * repeat re-assertions AND a settle window's own deadline) strictly
   * before `beforeT`, in chronological order (ties broken repeat-then-
   * settle, an arbitrary but deterministic choice -- the two are
   * independent mechanisms that can only coincide to within float
   * precision). Mirrors, for the settle window, exactly what the live
   * component's `setTimeout` (armed for `pending.startT + settleMs`, see
   * Joystick.tsx) does when the thumb holds still mid-window: the window
   * resolves on ITS OWN clock, not only when the next pointermove happens
   * to arrive. */
  function flushDueEvents(beforeT: number): void {
    for (;;) {
      const repeatDue = state.dir !== null && repeatNextAt !== null && repeatNextAt < beforeT ? repeatNextAt : null;
      const settleDeadline = state.pending !== null ? state.pending.startT + params.settleMs : null;
      const settleDue = settleDeadline !== null && settleDeadline < beforeT ? settleDeadline : null;
      if (repeatDue === null && settleDue === null) return;
      if (settleDue !== null && (repeatDue === null || settleDue <= repeatDue)) {
        const flush = flushPendingSettle(state.dir, state.pending);
        state = { ...state, dir: flush.dir, pending: null };
        if (flush.changed && flush.dir) {
          emissions.push({ t: settleDue, dir: flush.dir });
          repeatNextAt = settleDue + params.repeatMs;
        }
      } else if (repeatDue !== null) {
        emissions.push({ t: repeatDue, dir: state.dir! });
        repeatNextAt = repeatDue + params.repeatMs;
      }
    }
  }

  for (let i = 0; i < samples.length; i++) {
    const s = samples[i];
    if (i > 0) flushDueEvents(s.t);
    const result = updateJoystick(state, { x: s.x, y: s.y }, s.t, radius, params);
    state = result.state;
    details.push({ t: s.t, dir: result.dir, knob: result.knob, thumb: result.thumb });
    if (result.changed) {
      if (result.dir) {
        emissions.push({ t: s.t, dir: result.dir });
        repeatNextAt = s.t + params.repeatMs;
      } else {
        repeatNextAt = null;
      }
    }
  }
  // A settle window still open when the sample stream ends -- resolve it at
  // its own deadline, same as the live component's timer would if the
  // gesture's LAST sample happened to fall inside a still-pending window
  // (the offline replay has no "and then the thumb kept holding" tail to
  // observe, so this is the honest best-effort close: commit what the
  // window had already decided, at the time it would have decided it).
  if (state.pending !== null) {
    const deadline = state.pending.startT + params.settleMs;
    const flush = flushPendingSettle(state.dir, state.pending);
    if (flush.changed && flush.dir) emissions.push({ t: deadline, dir: flush.dir });
  }
  return { emissions, details };
}
